/** @jsxImportSource @emotion/react */
import "../App.css";
import "react-reflex/styles.css";
import "./UnassignedJobsView.css";
import {useAppContext} from "../ApplicationContext";
import {ManifestFilter, useSetUserPrefMutation} from "../generated/graphql";
import {ReflexContainer, ReflexElement, ReflexSplitter} from "react-reflex";
import {AuthContext} from "../providers/AuthProvider";
import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {Drawer, Position} from "@blueprintjs/core";
import {Fill} from "react-spaces";
import {LoadingSpinner} from "../components/common/LoadingSpinner";
import {UserClaims} from "@okta/okta-auth-js";
import {TenantPreferences, UserPreferences} from "../constants/constants";
import {AssignmentViewActionTypes, ManifestFilterTypePayload} from "../providers/AssignmentViewReducer";
import JobPanel, {AgJob} from "../components/job/JobPanel";
import ManifestDataProvider from "../components/manifest/providers/ManifestDataProvider";
import AddressResolverProvider from "../components/common/AddressResolverProvider";
import {MapVisibilityProvider} from "../components/map/MapVisibilityContext";
import ManifestDetailsChangeEventProvider from "../components/manifest/details/ManifestDetailsChangeEventProvider";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import styled from "@emotion/styled";
import _, {debounce} from "lodash";
import DispatchRulesDataProvider from "../providers/DispatchRulesDataProvider";
import {JobTypes} from "../components/settings/ColorSettings/ColorizedIndicators/types/jobTypeSelectorValues";
import {
  createJsonPref,
  extractJsonPref,
  extractSimplePref,
  usePreferenceContext
} from "../providers/PreferenceProvider";
import {ManifestFilterType} from "../components/manifest/list/components/manifest-filter/ManifestFilterState";
import AssignmentMapV2 from "../components/map/AssignmentMapV2";
import {FeatureFlagContext} from "../providers/FeatureFlagProvider";
import ListActionBar from "../components/common/ListActionBar";
import {useListActionBarDispatch} from "../components/common/ListActionBarProvider";
import {DateRange} from "@blueprintjs/datetime";
import {AgGridReact} from "@ag-grid-community/react";
import {MAP_PORTAL_ALERT, NewWindowPortalCommon} from "../components/common/NewWindowPortalCommon";
import ManifestDetailsContainer from "../components/manifest/details/ManifestDetailsContainer";
import {isSmallCard} from "../utils/General";
import {OptimizedStatusTypes, StatusType} from "../components/manifest/list/components/manifest-filter/ManifestFilter";
import ManifestDriverPanel from "../components/manifest/list/ManifestDriverPanel";
import GlobalFind from "../components/global-find/GlobalFind";
import {IManifestCardConfigurationsV2} from "../components/manifest/card-configuration/v2/types/manifest-card-configurations.type";
import {getDefaultCardConfigurations} from "../components/manifest/card-configuration/v2/constants/constants";
import {useAssignmentDispatch, useAssignmentState} from "../providers/AssignmentProdiver";
import AssignmentSubscription from "../components/common/AssignmentSubscription";
import {hasSelectedDispatchStations, useDispatchStationContext} from "../providers/DispatchStationProvider";
import {useDispatchGroupContext} from "../providers/DispatchGroupProvider";
import {makeQueryStringFilter} from "../utils/FilterUtils";
import log from "loglevel";
import {useManifestDetailsDispatch, useManifestDetailsState} from "../providers/ManifestDetailsProvider";
import {useAssignmentViewDispatch, useAssignmentViewState} from "../providers/AssignmentViewProvider";
import {useDispatchDataContext} from "../providers/DispatchDataProvider";
import {buildManifestDriverGroupFilter} from "../utils/ManifestUtils";
import {useSuggestedAssignment} from "../hooks/useSuggestedAssignment";
import SuggestedAssignmentProvider from "../providers/SuggestedAssignmentProvider";

export type TManifestDetailsDrawerOpen = (manifestDriverId: number, driverId: number) => void;

// initial and constant of width
const minimizeButtonPixelSize = 30;

const SPLITTER_WIDTH = 1;

export const buildManifestStatusFilter = (filter: ManifestFilter, statusTypes: StatusType[]) => {
  if (statusTypes.length > 0) {
    filter.manifest?.and?.push({
      or: statusTypes.map((status) => {
        return {manifestStatus: {match: status.charAt(0)}};
      })
    });
  } else {
    filter.manifest?.and?.push({
      or: [{manifestStatus: {match: "A"}}, {manifestStatus: {match: "I"}}, {manifestStatus: {match: "F"}}]
    });
  }
};

export const buildOptimizedStatusFilter = (filter: ManifestFilter, optimizedStatusTypes: OptimizedStatusTypes[]) => {
  if (optimizedStatusTypes.length === 1) {
    filter.manifest?.and?.push({
      optimizedDateTime: {exists: optimizedStatusTypes[0] === OptimizedStatusTypes.OPTIMIZED}
    });
  }
};

const UnassignedJobsView = () => {
  const {user} = useContext(AuthContext);
  const assignmentState = useAssignmentState();
  const assignmentDispatch = useAssignmentDispatch();
  const assignmentViewState = useAssignmentViewState();
  const assignmentViewDispatch = useAssignmentViewDispatch();
  const manifestDetailsState = useManifestDetailsState();
  const manifestDetailsStateDispatch = useManifestDetailsDispatch();
  const [setUserPref] = useSetUserPrefMutation();
  const {appState} = useAppContext();
  const {isDispatchDataLoaded} = useDispatchDataContext();
  const [minimumJobPanelWidth, setMinimumJobPanelWidth] = useState<number>(5);
  const [minimumMapPanelWidth, setMinimumMapPanelWidth] = useState<number>(5);
  const [mapWidth, setMapWidth] = useState<number>(0);
  const [mapMinimized, setMapMinimized] = useState<boolean>(false);
  const [jobWidth, setJobWidth] = useState<number>(0);
  const [jobMinimized, setJobMinimized] = useState<boolean>(false);
  const dispatchStationState = useDispatchStationContext();
  const {driverGroups} = useDispatchGroupContext();
  const [initRender, setInitRender] = useState<boolean>(true);
  const {
    skills: isSkillsEnabled,
    driverRequirements: driverRequirementsEnabled,
    suggestedAssignment: suggestedAssignmentEnabled
  } = useContext(FeatureFlagContext);
  const suggestedAssignment = useSuggestedAssignment(suggestedAssignmentEnabled);
  const {userPreferences, userPrefsQueryRefetch, isUserPreferencesLoaded, tenantPreferences} = usePreferenceContext();
  const listActionToolbarDispatch = useListActionBarDispatch();
  const [statusTypes, setStatusTypes] = useState<StatusType[]>([]);
  const [selectedOptimizedStatusTypes, setSelectedOptimizedStatusTypes] = useState<OptimizedStatusTypes[]>([]);

  const isUseETAService = extractSimplePref(tenantPreferences, TenantPreferences.useEtaService, true).value;

  const cardConfigurationsV2: IManifestCardConfigurationsV2 = useMemo(
    () =>
      extractJsonPref(
        userPreferences,
        UserPreferences.manifestCardConfigurations,
        getDefaultCardConfigurations(isUseETAService)
      ).value,
    [userPreferences, isUseETAService]
  );

  const cardTypeV2 = useMemo(() => cardConfigurationsV2.cardType, [cardConfigurationsV2.cardType]);

  const isSkillsBasedRequirements = useMemo(
    () => extractSimplePref(tenantPreferences, TenantPreferences.skillsBasedRequirements, false).value as boolean,
    [tenantPreferences]
  );

  const isSuggestionMode =
    suggestedAssignment.isLoading || suggestedAssignment.isActive || suggestedAssignment.state === "complete";

  const handleManifestDetailsDrawerOpen: TManifestDetailsDrawerOpen = useCallback(
    (manifestDriverId, driverId) => {
      if (!isSuggestionMode) {
        assignmentDispatch({type: "ClearSelections"});
        gridRef?.current?.api.deselectAll();
      }
      manifestDetailsStateDispatch({
        type: "SET_MANIFEST_DETAILS",
        payload: {
          manifestDriverId: manifestDriverId,
          driverId: driverId
        }
      });
    },
    [assignmentDispatch, manifestDetailsStateDispatch, isSuggestionMode]
  );

  const handleManifestDetailsDrawerClose = useCallback(() => {
    if (!isSuggestionMode) {
      gridRef?.current?.api.deselectAll();
      assignmentDispatch({type: "ClearSelections"});
    }
    listActionToolbarDispatch({type: "SET_IS_SHOWING", payload: false});
    manifestDetailsStateDispatch({
      type: "CLEAR_MANIFEST_DETAILS"
    });
  }, [assignmentDispatch, listActionToolbarDispatch, manifestDetailsStateDispatch, isSuggestionMode]);

  const buildManifestFilter = useCallback((): ManifestFilter => {
    const result: ManifestFilter = {
      manifest: {
        and: []
      }
    };
    buildManifestStatusFilter(result, statusTypes);
    buildOptimizedStatusFilter(result, selectedOptimizedStatusTypes);

    if (driverRequirementsEnabled && assignmentState.jobsRequirements.size > 0) {
      const fieldRequirementsFilter =
        isSkillsBasedRequirements && isSkillsEnabled ? "driver_skills_id" : "driver_qualifications_id";
      result.manifest?.and?.push({
        and: [...assignmentState.jobsRequirements.keys()].map((reqId) => {
          return {
            [fieldRequirementsFilter]: {term: reqId}
          };
        })
      });
    }

    if (hasSelectedDispatchStations(dispatchStationState)) {
      result.manifest?.and?.push(buildManifestDriverGroupFilter(driverGroups));
    }
    assignmentViewState.manifestFilters?.appendToFilter(result);

    // Suggested Assignment: filter to only show suggested manifest IDs
    if (suggestedAssignmentEnabled && suggestedAssignment.isActive) {
      const suggestedIds = suggestedAssignment.candidates.map((c) => c.manifestDriverId);
      result.manifest?.and?.push({
        or: suggestedIds.map((id) => ({manifestDriverId: {term: id}}))
      });
    }

    if (assignmentViewState.manifestQuery) {
      result.queryString = makeQueryStringFilter(assignmentViewState.manifestQuery, [
        "driver.name",
        "driver.code",
        "manifestDriverId",
        "stops.job.jobNumber",
        "stops.job.routeNumber"
      ]);
    }

    //log.debug("ManifestFilter: ", result);
    return result;
  }, [
    assignmentState.jobsRequirements,
    assignmentViewState.manifestFilters,
    assignmentViewState.manifestQuery,
    driverRequirementsEnabled,
    isSkillsBasedRequirements,
    isSkillsEnabled,
    selectedOptimizedStatusTypes,
    statusTypes,
    driverGroups,
    dispatchStationState,
    suggestedAssignmentEnabled,
    suggestedAssignment.isActive,
    suggestedAssignment.candidates
  ]);

  const persistUserPref = useCallback(
    async (key: string, value: any) => {
      await setUserPref({
        variables: {
          name: key,
          input: createJsonPref(value)
        }
      })
        .then(() => {
          userPrefsQueryRefetch?.();
        })
        .catch((error: Error) => {
          log.error(error);
        });
    },
    [setUserPref, userPrefsQueryRefetch]
  );

  const minimumManifestWidth = useMemo(() => (isSmallCard(cardTypeV2) ? 552 : 390), [cardTypeV2]);

  useEffect(() => {
    if (initRender && isUserPreferencesLoaded) {
      setMapMinimized("true" === extractSimplePref(userPreferences, UserPreferences.mapPanelMinimized, "false").value);
      setJobMinimized("true" === extractSimplePref(userPreferences, UserPreferences.jobPanelMinimized, "false").value);

      const jw = (totalWidth() - minimumManifestWidth - SPLITTER_WIDTH * 2) / 2;
      setJobWidth(extractSimplePref(userPreferences, UserPreferences.jobPanelSize, jw).value as number);

      setMapWidth(
        extractSimplePref(userPreferences, UserPreferences.mapPanelSize, totalWidth() - jw - minimumManifestWidth)
          .value as number
      );
      setInitRender(false);
    }
  }, [minimumManifestWidth, userPreferences, initRender, isUserPreferencesLoaded]);

  // expand manifest pane when select compact card mode
  useEffect(() => {
    const currentJobWidth = jobMinimized ? 8 : jobWidth;
    const currentMapWidth = mapMinimized ? 8 : mapWidth;

    const manifestWidth = totalWidth() - currentJobWidth - currentMapWidth - SPLITTER_WIDTH * 2;
    const widthToExpand = minimumManifestWidth - manifestWidth;

    if (widthToExpand > 0) {
      if (currentJobWidth - widthToExpand / 2 < 0) {
        setMapWidth((prev) => prev - widthToExpand);
        return;
      }
      if (currentMapWidth - widthToExpand / 2 < 0) {
        setJobWidth((prev) => prev - widthToExpand);
        return;
      }
      setMapWidth((prev) => prev - widthToExpand / 2);
      setJobWidth((prev) => prev - widthToExpand / 2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minimumManifestWidth]);

  //Handle when click minimize map button
  const handleMinimizeMap = async (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setMinimumMapPanelWidth(0);
    setMapMinimized(true);
    persistUserPref(UserPreferences.mapPanelMinimized, "true");
  };

  //Handle when click maximize map button
  const handleMaximizeMap = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setMinimumMapPanelWidth(5);
    setMapMinimized(false);
    persistUserPref(UserPreferences.mapPanelMinimized, "false");
  };

  //Handle when click minimize job button
  const handleMinimizeJob = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setMinimumJobPanelWidth(0);
    setJobMinimized(true);
    persistUserPref(UserPreferences.jobPanelMinimized, "true");
  };

  //Handle when click maximize job button
  const handleMaximizeJob = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setMinimumJobPanelWidth(5);
    setJobMinimized(false);
    persistUserPref(UserPreferences.jobPanelMinimized, "false");
  };

  //Handle when resizing map panel
  const handleMapPanelResize = useCallback(
    (e: any) => {
      const currentWidth = e?.domElement?.clientWidth + SPLITTER_WIDTH;
      setMapWidth(currentWidth);
      setUserPref({
        variables: {
          name: UserPreferences.mapPanelSize,
          input: createJsonPref(currentWidth)
        }
      })
        .then(() => {
          userPrefsQueryRefetch?.();
        })
        .catch((error: Error) => {
          log.error(error);
        });
    },
    [setUserPref, userPrefsQueryRefetch]
  );

  //Handle when resizing job panel
  const handleJobPaneResize = useCallback(
    (e: any) => {
      const currentWidth = e?.domElement?.clientWidth + SPLITTER_WIDTH;
      setJobWidth(currentWidth);
      setUserPref({
        variables: {
          name: UserPreferences.jobPanelSize,
          input: createJsonPref(currentWidth)
        }
      })
        .then(() => {
          userPrefsQueryRefetch?.();
        })
        .catch((error: Error) => {
          log.error(error);
        });
    },
    [setUserPref, userPrefsQueryRefetch]
  );

  const totalWidth = () => {
    return window.innerWidth;
  };

  //Calculate Job and Map flex
  const mapFlex = useMemo(() => {
    if (mapMinimized) {
      return minimumMapPanelWidth / totalWidth();
    } else {
      if (jobMinimized) {
        return (mapWidth + minimumJobPanelWidth) / totalWidth();
      }
      return +(mapWidth / totalWidth());
    }
  }, [jobMinimized, mapMinimized, mapWidth, minimumJobPanelWidth, minimumMapPanelWidth]);

  const jobFlex = useMemo(() => {
    if (jobMinimized) {
      return minimumJobPanelWidth / totalWidth();
    } else if (mapMinimized) {
      return (jobWidth + minimumMapPanelWidth) / totalWidth();
    } else {
      return jobWidth / totalWidth();
    }
  }, [jobMinimized, jobWidth, mapMinimized, minimumJobPanelWidth, minimumMapPanelWidth]);

  const onManifestFilterSelected = useCallback(
    (filterState: ManifestFilterTypePayload[], oldFilterState?: ManifestFilterTypePayload[]) => {
      if (oldFilterState && oldFilterState.length > 0) {
        assignmentViewDispatch({
          type: AssignmentViewActionTypes.CLEAR_MANIFEST_FILTER,
          payload: oldFilterState.map((filter) => filter.filterType)
        });
      }
      if (filterState.length > 0) {
        assignmentViewDispatch({
          type: AssignmentViewActionTypes.ADD_MANIFEST_FILTER,
          payload: filterState
        });
      } else {
        assignmentViewDispatch({
          type: AssignmentViewActionTypes.ADD_MANIFEST_FILTER,
          payload: {
            filterType: ManifestFilterType.NONE,
            value: undefined
          }
        });
      }
    },
    [assignmentViewDispatch]
  );

  const onManifestFilterDateChanged = useCallback(
    (dateRange: DateRange | undefined, selectedDateRange: DateRange | undefined) => {
      if (dateRange !== selectedDateRange) {
        setUserPref({
          variables: {
            name: UserPreferences.manifestDateRangeHeader,
            input: createJsonPref(dateRange, true)
          }
        }).then(() => {
          userPrefsQueryRefetch?.();
        });
        assignmentViewDispatch({
          type: AssignmentViewActionTypes.REPLACE_MANIFEST_FILTER_VALUE,
          payload: {filterType: ManifestFilterType.DATE_RANGE, value: dateRange}
        });
      }
    },
    [assignmentViewDispatch, setUserPref, userPrefsQueryRefetch]
  );

  const updateDriverGroupPref = useCallback(
    (arrayOfManifestFilter: React.MutableRefObject<number[]>) => {
      const driverGroupsSelected = extractJsonPref(userPreferences, UserPreferences.manifestGroupHeader, true)?.value;
      if (!_.isEqual(driverGroupsSelected, arrayOfManifestFilter.current)) {
        setUserPref({
          variables: {
            name: UserPreferences.manifestGroupHeader,
            input: createJsonPref(arrayOfManifestFilter.current, true)
          }
        }).then(() => {
          if (userPrefsQueryRefetch) userPrefsQueryRefetch();
        });
      }
    },
    [setUserPref, userPreferences, userPrefsQueryRefetch]
  );

  const onSelectDriverGroup = useCallback(
    (key: number, arrayOfManifestFilter: React.MutableRefObject<number[]>) => {
      updateDriverGroupPref(arrayOfManifestFilter);
      assignmentViewDispatch({
        type: AssignmentViewActionTypes.ADD_MANIFEST_FILTER,
        payload: {filterType: ManifestFilterType.DRIVER_GROUP, value: key}
      });
    },
    [assignmentViewDispatch, updateDriverGroupPref]
  );

  const onUnselectDriverGroup = useCallback(
    (key: number, arrayOfManifestFilter: React.MutableRefObject<number[]>) => {
      updateDriverGroupPref(arrayOfManifestFilter);
      assignmentViewDispatch({
        type: AssignmentViewActionTypes.REMOVE_MANIFEST_FILTER,
        payload: {filterType: ManifestFilterType.DRIVER_GROUP, value: key}
      });
    },
    [assignmentViewDispatch, updateDriverGroupPref]
  );

  const onReplaceDriverGroup = useCallback(
    (arrayOfManifestFilter: React.MutableRefObject<number[]>) => {
      updateDriverGroupPref(arrayOfManifestFilter);
      assignmentViewDispatch({
        type: AssignmentViewActionTypes.REPLACE_MANIFEST_FILTER_VALUES,
        payload: {filterType: ManifestFilterType.DRIVER_GROUP, value: arrayOfManifestFilter.current}
      });
    },
    [assignmentViewDispatch, updateDriverGroupPref]
  );

  const onUnselectAllDriverGroup = useCallback(
    (arrayOfManifestFilter: React.MutableRefObject<number[]>) => {
      updateDriverGroupPref(arrayOfManifestFilter);
      assignmentViewDispatch({
        type: AssignmentViewActionTypes.CLEAR_MANIFEST_FILTER,
        payload: ManifestFilterType.DRIVER_GROUP
      });
    },
    [assignmentViewDispatch, updateDriverGroupPref]
  );

  const onhandleManifestQuery = useCallback(
    (q: string) => {
      assignmentViewDispatch({
        type: AssignmentViewActionTypes.SET_MANIFEST_QUERY,
        payload: q
      });
    },
    [assignmentViewDispatch]
  );

  const gridRef = useRef<AgGridReact<AgJob>>();

  const getGridRef = useCallback((ref: AgGridReact<AgJob>) => {
    gridRef.current = ref;
  }, []);

  return (
    <LoadingSpinner loading={!appState.loaded || !isDispatchDataLoaded}>
      <Fill data-testid="jobAssignmentView">
        <AssignmentSubscription dispatch={assignmentDispatch} userClaims={user as UserClaims} />
        <SuggestedAssignmentProvider
          value={useMemo(
            () => ({
              ...suggestedAssignment,
              submit: (jobId: number) => suggestedAssignment.submit(jobId, buildManifestFilter())
            }),
            [suggestedAssignment, buildManifestFilter]
          )}
        >
          <MapVisibilityProvider>
            <ManifestDataProvider
              filter={buildManifestFilter()}
              sort={{
                field: assignmentViewState.manifestSort.property,
                direction: assignmentViewState.manifestSort.sortDirection
              }}
              skip={manifestDetailsState.manifestDriverId !== undefined}
            >
              <AddressResolverProvider>
                <ReflexContainer orientation="vertical">
                  <ReflexElement
                    minSize={minimumJobPanelWidth}
                    flex={jobFlex}
                    onStopResize={debounce(handleJobPaneResize, 150)}
                    style={{overflow: "visible"}}
                  >
                    <DispatchRulesDataProvider jobType={JobTypes.UnassignedJobs}>
                      <JobPanel
                        dragAndDropTargets={assignmentViewState.dragAndDropTargets}
                        currentDragAndDropTargets={assignmentViewState.currentDragAndDropTargets}
                        getGridRef={getGridRef}
                      />
                    </DispatchRulesDataProvider>
                    <MinimizeButton
                      className={"map-button"}
                      data-testid={jobMinimized ? "restore-job-button" : "reduce-job-button"}
                      onClick={jobMinimized ? handleMaximizeJob : handleMinimizeJob}
                      title={jobMinimized ? "Restore Job Area" : "Reduce Job Area"}
                      pixelOffset={jobMinimized ? 8 : 0}
                      side="right"
                    >
                      <MinimizeIcon
                        icon={jobMinimized ? solid("chevron-circle-right") : solid("chevron-circle-left")}
                      />
                    </MinimizeButton>
                  </ReflexElement>
                  <ReflexSplitter propagate={false} style={{pointerEvents: jobMinimized ? "none" : "auto"}}>
                    <div className="spaces-resize-handle resize-left" />
                  </ReflexSplitter>
                  <ReflexElement
                    flex={appState.isMapPortalOpen ? 1 - jobFlex : undefined}
                    minSize={minimumManifestWidth}
                    style={{minWidth: `${minimumManifestWidth}px`}}
                  >
                    <ManifestDriverPanel
                      setStatusTypes={setStatusTypes}
                      statusTypes={statusTypes}
                      selectedOptimizedStatusTypes={selectedOptimizedStatusTypes}
                      setSelectedOptimizedStatusTypes={setSelectedOptimizedStatusTypes}
                      onManifestDetailsDrawerOpen={handleManifestDetailsDrawerOpen}
                      filterSelected={onManifestFilterSelected}
                      filterDateChanged={onManifestFilterDateChanged}
                      selectDriverGroup={onSelectDriverGroup}
                      unselectDriverGroup={onUnselectDriverGroup}
                      replaceDriverGroup={onReplaceDriverGroup}
                      unselectAllDriverGroup={onUnselectAllDriverGroup}
                      handleManifestQuery={onhandleManifestQuery}
                    />
                  </ReflexElement>
                  {appState.isMapPortalOpen && (
                    <NewWindowPortalCommon
                      target="map"
                      title="Dispatch Map"
                      alertTextWhenOpen={MAP_PORTAL_ALERT.WHEN_OPEN}
                      alertTextWhenPaused={MAP_PORTAL_ALERT.WHEN_PAUSE}
                    >
                      <AssignmentMapV2
                        gridRef={gridRef}
                        mapResponsiveWidth={mapWidth}
                        onManifestDetailsDrawerOpen={handleManifestDetailsDrawerOpen}
                      />
                    </NewWindowPortalCommon>
                  )}
                  <ReflexSplitter propagate={false} style={{pointerEvents: mapMinimized ? "none" : "auto"}}>
                    <div className="spaces-resize-handle resize-left" />
                  </ReflexSplitter>
                  {!appState.isMapPortalOpen && (
                    <ReflexElement
                      flex={mapFlex}
                      onStopResize={debounce(handleMapPanelResize, 150)}
                      minSize={minimumMapPanelWidth}
                      style={{overflow: "visible"}}
                    >
                      <MinimizeButton
                        className={"map-button"}
                        data-testid={mapMinimized ? "restore-map-button" : "reduce-map-button"}
                        onClick={mapMinimized ? handleMaximizeMap : handleMinimizeMap}
                        title={mapMinimized ? "Restore Map Area" : "Reduce Map Area"}
                        pixelOffset={mapMinimized ? 8 : 0}
                        side="left"
                      >
                        <MinimizeIcon
                          icon={mapMinimized ? solid("chevron-circle-left") : solid("chevron-circle-right")}
                        />
                      </MinimizeButton>
                      <AssignmentMapV2
                        gridRef={gridRef}
                        mapResponsiveWidth={mapWidth}
                        onManifestDetailsDrawerOpen={handleManifestDetailsDrawerOpen}
                      />
                    </ReflexElement>
                  )}
                </ReflexContainer>
              </AddressResolverProvider>
            </ManifestDataProvider>
          </MapVisibilityProvider>
        </SuggestedAssignmentProvider>
        <DispatchRulesDataProvider jobType={JobTypes.AssignedStops}>
          <Drawer
            className="drawer_container"
            isOpen={manifestDetailsState.manifestDriverId !== undefined}
            onClose={handleManifestDetailsDrawerClose}
            canEscapeKeyClose
            enforceFocus={false}
            canOutsideClickClose={false}
            hasBackdrop={false}
            size="100%"
            position={Position.LEFT}
            portalClassName="manifest-details-portal"
          >
            {manifestDetailsState.manifestDriverId && (
              <ManifestDetailsChangeEventProvider manifestDriverId={manifestDetailsState.manifestDriverId}>
                <MapVisibilityProvider>
                  <ManifestDetailsContainer
                    manifestDriverId={manifestDetailsState.manifestDriverId}
                    onClose={handleManifestDetailsDrawerClose}
                  />
                  <ListActionBar />
                </MapVisibilityProvider>
              </ManifestDetailsChangeEventProvider>
            )}
          </Drawer>
        </DispatchRulesDataProvider>
        {appState.isGlobalFindOpen && <GlobalFind />}
      </Fill>
    </LoadingSpinner>
  );
};
export default UnassignedJobsView;

const MinimizeIcon = styled(FontAwesomeIcon)`
  width: ${minimizeButtonPixelSize * 0.75}px;
  height: ${minimizeButtonPixelSize * 0.75}px;
  border-radius: 50%;
  color: #14305a;
  background-color: darkgray;

  &:active,
  &:hover {
    background-color: #ffffff;
  }

  transition: background-color 0.5s;
`;

type minimizeButtonProps = {
  pixelOffset?: number;
  side: "left" | "right";
};

const MinimizeButton = styled.div<minimizeButtonProps>`
  position: absolute;
  top: 50%;
  left: ${(props) =>
    props.side === "left" ? ({pixelOffset = -2}) => `${-pixelOffset - minimizeButtonPixelSize / 2}px` : ""};
  right: ${(props) =>
    props.side === "right" ? ({pixelOffset = -2}) => `${-pixelOffset - minimizeButtonPixelSize / 2}px` : ""};
  cursor: pointer;
  width: ${minimizeButtonPixelSize}px;
  height: ${minimizeButtonPixelSize}px;

  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;

  background-color: #14305a;
  overflow: hidden;

  border-radius: 50%;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.25);

  transform: translateY(-2px);

  ${"&:active&:hover" /* prevent intellij format bug from inserting space between &:active and &:hover */} {
    transform: translateY(0);
  }
`;
